import { useCallback, useEffect, useRef, useState } from 'react'

import { DataModeBadge, HeaderBand } from '../components/HeaderBand'

// ============================================================================
// THE SCRATCHPAD — one box, one browser, no server.
//
// WHAT THE OWNER ASKED FOR (2026-09-01): "a little temporary place to write
// text ... super simple. It doesn't even need to have an API."
//
// WHY IT IS ALLOWED TO HAVE NO API, when the product rule is that every feature
// exposes its data through the Worker. The rule exists so that nothing the owner
// RECORDS becomes invisible to an LLM later — a journal entry, a lecture, a
// reminder. This is the opposite kind of surface: a holding pen for a thought
// that has not decided what it is yet. The moment one of these is worth keeping
// it belongs in the journal or in Reminders, both of which are indexed. Giving
// the pad a table and a contract would make it a fifth place where real notes go
// to be forgotten, which is the failure mode the machine-readability rule is
// there to prevent in the first place.
//
// Same precedent, same shape as lib/journal-prefs.ts and lib/scale.ts: one
// namespaced localStorage key, read and written here, never sent anywhere.
//
// AND SO IT SAYS SO, ON THE SCREEN. A box that looks like the rest of LifeOS but
// does not sync to the phone and does not appear in search would be the app
// faking knowledge it does not have. The footer states all three limits in
// words: this browser only, not searchable, not backed up.
//
// THE UNDO CONTRACT (docs — no gate can see this). The textarea is UNCONTROLLED
// (`defaultValue`, never `value`). A controlled input whose value is re-set by an
// autosave destroys the browser's native undo stack after the first save. If you
// ever make this controlled, check by hand: type, wait for "Saved", press Ctrl+Z.
// ============================================================================

/** Namespaced like THEME_STORAGE_KEY and the journal prefs. */
export const SCRATCHPAD_STORAGE_KEY = 'lifeos.scratchpad.text'

/**
 * Long enough not to write on every keystroke, short enough that closing the tab
 * mid-thought loses nothing — the same debounce the journal composer uses.
 */
const AUTOSAVE_MS = 500

export function readScratchpad(storage: Storage): string {
  try {
    return storage.getItem(SCRATCHPAD_STORAGE_KEY) ?? ''
  } catch {
    // Private browsing, or storage disabled entirely. An empty pad that cannot
    // remember is still a usable pad; a screen that throws is not.
    return ''
  }
}

export function writeScratchpad(storage: Storage, text: string): boolean {
  try {
    storage.setItem(SCRATCHPAD_STORAGE_KEY, text)
    return true
  } catch {
    return false
  }
}

/** Words the way the journal counts them, so the two numbers mean the same thing. */
export function countWords(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0
}

type SaveState = 'idle' | 'saving' | 'saved' | 'failed'

export function Scratchpad() {
  // Read ONCE, for the uncontrolled textarea's initial value. Re-reading on every
  // render would fight the box the owner is typing into.
  const [initial] = useState(() => readScratchpad(window.localStorage))
  const [words, setWords] = useState(() => countWords(initial))
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pending = useRef<string | null>(null)

  // THE KEYSTROKE PROMISE, the same one the journal composer makes: a debounce is
  // a window in which the text exists only in the DOM. Closing the tab or
  // switching apps lands inside that window eventually, so both flush it.
  const flushRef = useRef<() => void>(() => {})
  flushRef.current = () => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    const text = pending.current
    if (text === null) return
    pending.current = null
    setSaveState(writeScratchpad(window.localStorage, text) ? 'saved' : 'failed')
  }

  useEffect(() => {
    const flush = () => flushRef.current()
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    document.addEventListener('visibilitychange', onHidden)
    window.addEventListener('pagehide', flush)
    return () => {
      document.removeEventListener('visibilitychange', onHidden)
      window.removeEventListener('pagehide', flush)
      flush() // leaving the screen counts as leaving too
    }
  }, [])

  const onType = useCallback((text: string) => {
    setWords(countWords(text))
    setSaveState('saving')
    pending.current = text
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      timer.current = null
      pending.current = null
      setSaveState(writeScratchpad(window.localStorage, text) ? 'saved' : 'failed')
    }, AUTOSAVE_MS)
  }, [])

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <HeaderBand tall>
        <div className="flex min-w-0 flex-col gap-[5px]">
          <h1 className="text-[16px] font-semibold leading-none tracking-[-0.01em]">Scratchpad</h1>
          <p className="text-[11.5px] font-normal leading-none text-microcopy">
            A pocket for loose thoughts
          </p>
        </div>
        <div className="ml-auto flex flex-none items-center gap-2">
          <DataModeBadge />
        </div>
      </HeaderBand>

      <div className="flex min-h-0 flex-1 flex-col px-[30px] pb-[26px] pt-4">
        <section className="flex min-h-0 flex-1 flex-col rounded-context border border-line bg-surface px-[30px] pb-5 pt-6 shadow-card">
          <header className="flex flex-none items-center gap-[10px]">
            <span className="text-[11px] font-semibold leading-none tracking-[0.06em] text-muted">
              SCRATCHPAD
            </span>
            <SaveChip state={saveState} />
          </header>

          {/* Wrapped in the journal composer's own field class (see JournalEntryCard) rather than
              left as a bare `outline-none` box: 12d's text-entry rule applies to every composer,
              not just the journal's, and without the wrapper there is no focus indicator at
              all — an accessibility regression, not a style miss. */}
          <div className="mo-field-journal mt-[14px] min-h-[168px] w-full flex-1">
            <textarea
              defaultValue={initial}
              onChange={(event) => onType(event.target.value)}
              placeholder="Anything. It stays here until you clear it."
              aria-label="Scratchpad"
              className="size-full resize-none border-0 bg-transparent p-0 text-[16.5px] leading-[1.72] text-ink-2 outline-none placeholder:font-normal placeholder:text-microcopy text-pretty"
            />
          </div>

          <div className="mt-4 flex flex-none flex-wrap items-center gap-x-7 gap-y-2">
            <span className="rounded-pill bg-fill-2 px-3 py-[6px] text-[11.5px] font-medium leading-[1.3] text-ink-2">
              {words} {words === 1 ? 'word' : 'words'}
            </span>
            {/* THE THREE LIMITS, in words. Everything else in LifeOS syncs and is
                searchable; this does neither, and a pad that looked the same
                while behaving differently is the app faking knowledge. */}
            <span className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
              This browser only — it does not sync to your phone, it is not in search, and it is
              not backed up.
            </span>
          </div>
        </section>
      </div>
    </div>
  )
}

/** Says what it knows and nothing more — never "Saved" for a write that failed. */
function SaveChip({ state }: { state: SaveState }) {
  if (state === 'idle') return null
  if (state === 'failed') {
    return (
      <span className="text-[11px] font-semibold leading-none text-danger">
        This browser refused to store it
      </span>
    )
  }
  return (
    <span className="text-[11px] font-semibold leading-none text-success">
      {state === 'saving' ? 'Saving…' : 'Saved'}
    </span>
  )
}
